import React, { useContext, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styles from './EditTaskDialog.module.scss';
import DeviceContext from '../context/DeviceContext.js';
import UserContext from '../context/UserContext.js';
import { useMutation, useQuery, gql } from '@apollo/client';
import { GET_PROJECTS_QUERY, DELETE_TASK_MUTATION, GET_TASK_OUTPUTS_QUERY } from '../query/gqlQueries.js';

// Rewrite a server-baked thumbnail origin to the client's image host, and build
// a direct file URL — same scheme as MediaView.
const IMAGE_STORE_URI = process.env.REACT_APP_IMAGE_STORE_URI;
const rewriteThumb = (url) => {
  if (!url || !IMAGE_STORE_URI) return url;
  try { const u = new URL(url); return IMAGE_STORE_URI.replace(/\/$/, '') + u.pathname + u.search; } catch { return url; }
};
const outputFileUrl = (p) =>
  IMAGE_STORE_URI ? `${IMAGE_STORE_URI.replace(/\/$/, '')}/media/file/${String(p).split('/').map(encodeURIComponent).join('/')}` : null;

import BrushIcon from '@mui/icons-material/Brush';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import CodeIcon from '@mui/icons-material/Code';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SchoolIcon from '@mui/icons-material/School';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import VideocamIcon from '@mui/icons-material/Videocam';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

import DavidImage from '../images/David.jpg';
import SelinayImage from '../images/Selinay.jpg';
import TomasImage from '../images/Tomas.jpg';

const ADD_TASK_MUTATION = gql`
  mutation AddTask(
    $title: String!
    $type: String!
    $description: String
    $position: String!
    $priority: String!
    $assignee: String
    $timeBudget: Float
    $timeSpent: Float
    $dueDate: DateTime!
    $username: String!
    $project: String
  ) {
    addTask(
      input: {
        title: $title
        type: $type
        description: $description
        position: $position
        priority: $priority
        assignee: $assignee
        timeBudget: $timeBudget
        timeSpent: $timeSpent
        dueDate: $dueDate
        username: $username
        project: $project
      }
    ) {
      identity
      elementId
      properties {
        title
        type
        description
        priority
        assignee
        timeBudget
        timeSpent
        dueDate
        username
        project
      }
    }
  }
`;

const UPDATE_TASK_MUTATION = gql`
  mutation UpdateTask($identity: ID!, $input: TaskUpdateInput!) {
    updateTask(identity: $identity, input: $input) {
      identity
      elementId
      properties {
        title
        type
        description
        priority
        assignee
        timeBudget
        timeSpent
        dueDate
        username
        project
      }
    }
  }
`;

// Returns a Date advanced by `n` working days (Mon-Fri), starting from `from`.
// Saturday → +2 lands on Tuesday, Friday → +2 lands on Tuesday, etc.
function addWorkingDays(from, n) {
  const d = new Date(from);
  let added = 0;
  while (added < n) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay(); // 0 Sun … 6 Sat
    if (dow !== 0 && dow !== 6) added++;
  }
  return d;
}

// Default due date for a brand-new task: 2 working days ahead, bumped to 3
// if it's already past 4pm (you wouldn't realistically finish today's slot
// of work). Time defaults to 16:00.
function defaultDueDateTime() {
  const now = new Date();
  const after4pm = now.getHours() >= 16;
  const due = addWorkingDays(now, after4pm ? 3 : 2);
  return {
    year:  String(due.getFullYear()),
    month: String(due.getMonth() + 1).padStart(2, '0'),
    day:   String(due.getDate()).padStart(2, '0'),
    time:  '16:00',
  };
}

export default function EditTaskDialog({ isOpen, onClose, task }) {
  const { isMobile } = useContext(DeviceContext) || { isMobile: false };
  const userContext = useContext(UserContext);
  const username = userContext?.username || "David";

  const [title, setTitle] = useState('Add Task');
  const [description, setDescription] = useState('');
  const [position, setPosition] = useState('Backlog');
  const [priority, setPriority] = useState('Low');
  const [assignee, setAssignee] = useState('David');
  const [timeBudget, setTimeBudget] = useState(3);
  const [timeSpent, setTimeSpent] = useState(0);
  const [type, setType] = useState('Admin');
  const [dueYear, setDueYear] = useState('');
  const [dueMonth, setDueMonth] = useState('');
  const [dueDay, setDueDay] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [project, setProject] = useState('');
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const { data: projectsData } = useQuery(GET_PROJECTS_QUERY, {
    variables: { username },
    skip: !username,
  });
  const projectsList = projectsData?.getProjects || [];

  // Media outputs this task produced (deliverables linked from the Media area).
  const { data: outputsData } = useQuery(GET_TASK_OUTPUTS_QUERY, {
    variables: { taskId: task?.identity },
    skip: !isOpen || !task?.identity,
    fetchPolicy: 'cache-and-network',
  });
  const outputs = outputsData?.getTaskOutputs || [];

  const [addTask, { loading: adding }] = useMutation(ADD_TASK_MUTATION);
  const [updateTask, { loading: updating }] = useMutation(UPDATE_TASK_MUTATION);
  const [deleteTaskMutation, { loading: deleting }] = useMutation(DELETE_TASK_MUTATION);
  
  const loading = adding || updating || deleting;

  useEffect(() => {
    if (task && isOpen) {
      const p = task.properties || {};
      setTitle(p.title || '');
      setDescription(p.description || '');
      // API returns ALL-CAPS (INPROGRESS, DONE, …); position buttons use mixed-case
      const statusToPosition = {
        BACKLOG:    'Backlog',
        INPROGRESS: 'InProgress',
        VALIDATING: 'Validating',
        DONE:       'Done',
      };
      const rawStatus = task.status ? task.status.toUpperCase() : '';
      setPosition(statusToPosition[rawStatus] || 'Backlog');
      setPriority(p.priority || 'Low');
      setAssignee(p.assignee || 'David');
      setTimeBudget(p.timeBudget != null ? p.timeBudget : 3);
      setTimeSpent(p.timeSpent != null ? p.timeSpent : 0);
      setType(p.type || 'Admin');
      setProject(p.project || '');
      
      if (p.dueDate) {
        try {
          console.log("Raw dueDate from DB:", p.dueDate);
          let dateStr = p.dueDate;
          // Handle neo4j object if somehow returned as an object
          if (typeof p.dueDate === 'object' && p.dueDate.year) {
            dateStr = `${p.dueDate.year.low || p.dueDate.year}-${String(p.dueDate.month.low || p.dueDate.month).padStart(2, '0')}-${String(p.dueDate.day.low || p.dueDate.day).padStart(2, '0')}T${String(p.dueDate.hour?.low || p.dueDate.hour || 0).padStart(2, '0')}:${String(p.dueDate.minute?.low || p.dueDate.minute || 0).padStart(2, '0')}:00Z`;
          } else if (typeof p.dueDate === 'string') {
            dateStr = p.dueDate.endsWith('Z') ? p.dueDate : p.dueDate + 'Z';
          }
          
          console.log("Normalized date string:", dateStr);
          const d = new Date(dateStr);
          console.log("Parsed JS Date:", d);
          
          // Format as local YYYY-MM-DD
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          setDueYear(year.toString());
          setDueMonth(month);
          setDueDay(day);
          
          // Format as local HH:mm
          const hours = String(d.getHours()).padStart(2, '0');
          const minutes = String(d.getMinutes()).padStart(2, '0');
          setDueTime(`${hours}:${minutes}`);
        } catch (e) {
          console.error("Error parsing date:", e);
        }
      } else {
        setDueYear('');
        setDueMonth('');
        setDueDay('');
        setDueTime('');
      }
      setIsConfirmingDelete(false);
    } else if (!task && isOpen) {
      setTitle('Add Task');
      setDescription('');
      setPosition('Backlog');
      setPriority('Low');
      setAssignee('David');
      setTimeBudget(3);
      setTimeSpent(0);
      setType('Admin');
      setProject('');
      // Smart default due date: 2 working days ahead at 4pm
      // (bumped to 3 working days if it's already past 4pm today).
      const due = defaultDueDateTime();
      setDueYear(due.year);
      setDueMonth(due.month);
      setDueDay(due.day);
      setDueTime(due.time);
      setIsConfirmingDelete(false);
    }
  }, [task, isOpen]);

  const handleSave = async () => {
    let combinedDate = new Date().toISOString();
    if (dueYear && dueMonth && dueDay) {
       const dateOnly = `${dueYear}-${dueMonth}-${dueDay}`;
       combinedDate = dueTime ? `${dateOnly}T${dueTime}:00` : `${dateOnly}T00:00:00`;
    }
    
    try {
      if (task && task.identity) {
        await updateTask({
          variables: {
            identity: task.identity,
            input: {
              title,
              type,
              description,
              position,
              priority,
              assignee,
              timeBudget: timeBudget ? parseFloat(timeBudget) : 0,
              timeSpent: timeSpent ? parseFloat(timeSpent) : 0,
              dueDate: combinedDate,
              username,
              project: project || null
            }
          }
        });
      } else {
        await addTask({
          variables: {
            title,
            type,
            description,
            position,
            priority,
            assignee,
            timeBudget: timeBudget ? parseFloat(timeBudget) : 0,
            timeSpent: timeSpent ? parseFloat(timeSpent) : 0,
            dueDate: combinedDate,
            username,
            project: project || null
          }
        });
      }
      onClose();
    } catch (e) {
      console.error("Error saving task:", e);
    }
  };

  const handleDelete = async () => {
    if (!isConfirmingDelete) {
      setIsConfirmingDelete(true);
      return;
    }
    try {
      await deleteTaskMutation({
        variables: { identity: task.identity }
      });
      setIsConfirmingDelete(false);
      onClose();
    } catch (e) {
      console.error("Error deleting task:", e);
    }
  };

  const positions = [
    { label: 'Backlog', value: 'Backlog' },
    { label: 'Doing', value: 'InProgress' },
    { label: 'Validating', value: 'Validating' },
    { label: 'Completed', value: 'Done' }
  ];

  const priorities = [
    { color: styles.green, value: 'Low' },
    { color: styles.yellow, value: 'Medium' },
    { color: styles.red, value: 'High' },
    { color: styles.darkRed, value: 'Urgent' }
  ];

  const assignees = [
    { name: 'David', image: DavidImage },
    { name: 'Selinay', image: SelinayImage },
    { name: 'Tomas', image: TomasImage }
  ];

  const types = [
    { label: "Design", icon: <BrushIcon fontSize="small" /> },
    { label: "3D", icon: <ViewInArIcon fontSize="small" /> },
    { label: "Coding", icon: <CodeIcon fontSize="small" /> },
    { label: "Admin", icon: <AssignmentIcon fontSize="small" /> },
    { label: "Learning", icon: <SchoolIcon fontSize="small" /> },
    { label: "Photography", icon: <PhotoCameraIcon fontSize="small" /> },
    { label: "Video", icon: <VideocamIcon fontSize="small" /> }
  ];

  const getDeadlineText = () => {
    if (!dueYear || !dueMonth || !dueDay) return null;
    const now = new Date();
    // Reset times to compare dates accurately
    now.setHours(0, 0, 0, 0);
    const dateOnly = `${dueYear}-${dueMonth}-${dueDay}`;
    const due = new Date(dateOnly);
    due.setHours(0, 0, 0, 0);
    
    const diffTime = due - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Overdue';
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    if (diffDays < 7) return `Due in ${diffDays} days`;
    
    const weeks = Math.floor(diffDays / 7);
    return `Due in ${weeks} week${weeks > 1 ? 's' : ''}`;
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={onClose}>
          <CloseIcon />
        </button>
        <div className={styles.header}>
          <div className={styles.titleGroup}>
            <div className={styles.label}>Task Manager</div>
            <h2 className={styles.title}>{task ? 'Edit Task' : 'Add Task'}</h2>
          </div>
        </div>

        <div className={styles.containerGrid}>
          {/* Project */}
          <div className={`${styles.field} ${styles.projectArea}`}>
            <div className={styles.fieldLabel}>Project</div>
            <div className={styles.chipBox}>
              <select 
                className={styles.input} 
                value={project} 
                onChange={(e) => setProject(e.target.value)}
              >
                <option value="">None</option>
                {projectsList.map(proj => (
                  <option key={proj.identity} value={proj.properties.title}>
                    {proj.properties.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {/* Title */}
          <div className={`${styles.field} ${styles.titleArea}`}>
            <div className={styles.fieldLabel}>Title</div>
            <input type="text" className={styles.input} value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>

          {/* Priority (Colors) */}
          <div className={`${styles.field} ${styles.priorityArea}`}>
            <div className={styles.fieldLabel}>Priority</div>
            <div className={styles.chipBox}>
              <div className={styles.colorCircles}>
                {priorities.map(p => (
                  <div 
                    key={p.value} 
                    className={`${styles.circle} ${p.color} ${priority === p.value ? styles.selectedCircle : ''}`}
                    onClick={() => setPriority(p.value)}
                  ></div>
                ))}
              </div>
            </div>
          </div>

          {/* Assignee (Avatars) */}
          <div className={`${styles.field} ${styles.assigneeArea}`}>
            <div className={styles.fieldLabel}>{isMobile ? 'Priority' : 'Assignee'}</div>
            <div className={styles.chipBox}>
              <div className={styles.avatars}>
                {assignees.map(a => (
                  <div 
                    key={a.name} 
                    className={`${styles.avatarWrapper} ${assignee === a.name ? styles.selected : ''}`}
                    onClick={() => setAssignee(a.name)}
                    title={a.name}
                  >
                    <img src={a.image} alt={a.name} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Description */}
          <div className={`${styles.field} ${styles.descriptionArea}`}>
            <div className={styles.fieldLabel}>Description</div>
            <textarea className={styles.input} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>

          {/* Time Budget & Spent */}
          <div className={`${styles.field} ${styles.timeArea}`}>
            <div className={styles.rowLabels}>
              <div className={styles.fieldLabel}>Time Budget</div>
              <div className={styles.fieldLabel}>Spent</div>
            </div>
            <div className={styles.rowInputs}>
              <div className={styles.chipBox}>
                <input type="number" className={`${styles.input} ${styles.centered}`} value={timeBudget} onChange={(e) => setTimeBudget(e.target.value)} />
              </div>
              <div className={styles.chipBox}>
                <input type="number" className={`${styles.input} ${styles.centered}`} value={timeSpent} onChange={(e) => setTimeSpent(e.target.value)} />
              </div>
            </div>
          </div>

          {/* Position */}
          <div className={`${styles.field} ${styles.positionArea}`}>
            <div className={styles.fieldLabel}>Position</div>
            <div className={styles.positionGrid}>
              {positions.map(p => (
                <button 
                  key={p.value} 
                  className={`${styles.positionBtn} ${position === p.value ? styles.selectedBtn : ''}`}
                  onClick={() => setPosition(p.value)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Type */}
          <div className={`${styles.field} ${styles.typeArea}`}>
            <div className={styles.fieldLabel}>Type</div>
            <div className={styles.typeGrid}>
              {types.map(t => (
                <button 
                  key={t.label} 
                  className={`${styles.typeBtn} ${type === t.label ? styles.selectedBtn : ''}`}
                  onClick={() => setType(t.label)}
                >
                  {t.icon}
                  <span>{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Due Date */}
          <div className={`${styles.field} ${styles.dateArea}`}>
            <div className={styles.fieldLabel} style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Due Date</span>
              {getDeadlineText() && <span className={styles.deadlineNote}>{getDeadlineText()}</span>}
            </div>
            <div className={styles.rowInputsDate}>
              <div className={`${styles.chipBox} ${styles.timeBox}`}>
                <AccessTimeIcon fontSize="small" className={styles.inputIcon} />
                <input type="time" className={styles.input} value={dueTime} onChange={(e) => setDueTime(e.target.value)} />
              </div>
              <div className={`${styles.chipBox} ${styles.dateBox}`}>
                <CalendarMonthIcon fontSize="small" className={styles.inputIcon} />
                <div className={styles.dateSelects}>
                  <select className={styles.input} value={dueDay} onChange={e => setDueDay(e.target.value)}>
                    <option value="">DD</option>
                    {Array.from({length: 31}, (_, i) => String(i + 1).padStart(2, '0')).map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                  <select className={styles.input} value={dueMonth} onChange={e => setDueMonth(e.target.value)}>
                    <option value="">MM</option>
                    {Array.from({length: 12}, (_, i) => String(i + 1).padStart(2, '0')).map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                  <select className={styles.input} value={dueYear} onChange={e => setDueYear(e.target.value)}>
                    <option value="">YYYY</option>
                    {Array.from({length: 10}, (_, i) => String(new Date().getFullYear() + i - 1)).map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Outputs — media deliverables linked to this task */}
        {task && task.identity && (
          <div style={{ padding: '0 1.5rem 0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#94a3b8' }}>Outputs</span>
              <span style={{ fontSize: '0.7rem', color: '#555560' }}>linked from the Media area</span>
            </div>
            {outputs.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
                {outputs.map((o) => (
                  <a
                    key={o.identity}
                    href={outputFileUrl(o.path)}
                    target="_blank"
                    rel="noreferrer"
                    title={`${o.filename} — open`}
                    style={{ width: '92px', textDecoration: 'none' }}
                  >
                    <div style={{ width: '92px', height: '68px', borderRadius: '8px', overflow: 'hidden', background: '#0a0a0c', border: '1px solid #2a2a30', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {o.thumbnailUrl
                        ? <img src={rewriteThumb(o.thumbnailUrl)} alt={o.filename} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <span style={{ fontSize: '0.6rem', color: '#94a3b8', textTransform: 'uppercase' }}>{o.kind || 'file'}</span>}
                    </div>
                    <div style={{ fontSize: '0.62rem', color: '#cbd5e1', marginTop: '0.2rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: "'DM Mono', monospace" }}>{o.filename}</div>
                  </a>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: '0.78rem', color: position === 'Done' ? '#fbbf24' : '#555560', lineHeight: 1.4 }}>
                {position === 'Done'
                  ? '⚑ Marked done, but no output linked yet — open the exported file in the Media area and choose “Link to task”.'
                  : 'When this is finished, link its exported file(s) from the Media area so the result lives with the task.'}
              </div>
            )}
          </div>
        )}

        <div className={styles.footer}>
          {task && (
            <button 
              className={isConfirmingDelete ? styles.dangerBtn : styles.deleteBtn} 
              onClick={handleDelete} 
              disabled={loading}
            >
              {isConfirmingDelete ? 'Confirm you want to delete this permanently' : 'Delete Task'}
            </button>
          )}
          <button className={styles.primaryBtn} onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : 'Save Task'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
